import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import { Container } from "reactstrap";
import ActionMenu from "../../../components/Common/ActionMenu";
import { get, del } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";

/* -------------------------------------------------------------------------
 * AdminUsersPage — full "Users/Members" table view with CRUD functionality.
 * Provides listing, search, sort, pagination, create, edit, and delete operations.
 * ---------------------------------------------------------------------- */

const AdminUsersPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Active Members | Omaha Food Lovers";
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      width: "1.5fr",
      render: (row) => (
        <Link
          to={`/admin/users/${row.id}/view`}
          state={{ userData: row }}
          style={{
            color: '#000',
            textDecoration: 'none',
            fontWeight: '500',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.color = '#ec421c'}
          onMouseLeave={(e) => e.target.style.color = '#000'}
        >
          {row.name}
        </Link>
      )
    },
    { key: "email", label: "Email", width: "1.5fr" },
    { key: "status", label: "Status", width: "0.8fr", render: (row) => (row.isActive ? "Active" : "Inactive") },
    { key: "pointsBalance", label: "Points Balance", width: "1fr" },
    { key: "linkedCardsCount", label: "Linked Cards", width: "1fr" },
    { key: "vouchersCount", label: "Vouchers", width: "1fr" },
    { key: "referralsCount", label: "Referrals", width: "1fr" },
    { key: "gamesCompleted", label: "Games Completed", width: "1fr" },
    {
      key: "actions",
      label: "",
      width: "60px",
      align: "right",
      className: "rp-table__cell--actions",
      render: (row) => (
        <ActionMenu
          isOpen={activeDropdown === row.id}
          onToggle={() => setActiveDropdown(activeDropdown === row.id ? null : row.id)}
          items={[
            {
              label: "View",
              icon: "view",
              onClick: () => navigate(`/admin/users/${row.id}/view`, { state: { userData: row } }),
            },
            {
              label: "Edit",
              icon: "edit",
              onClick: () => navigate(`/admin/users/${row.id}/edit`, { state: { userData: row } }),
            },
            {
              label: "Delete",
              icon: "delete",
              onClick: () => handleDelete(row.id),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);
    params.append("member_type", "customer");

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const transformUserData = (usersArray) =>
    usersArray.map((userItem) => ({
      id: userItem.id,
      firstName: userItem.first_name || "",
      lastName: userItem.last_name || "",
      name: userItem.full_name || `${userItem.first_name || ""} ${userItem.last_name || ""}`.trim() || "Unknown",
      email: userItem.email || "N/A",
      mobile: userItem.mobile || "N/A",
      isAdmin: userItem.is_admin === "1" || userItem.is_admin === 1,
      isFreeMember: userItem.is_free_member === 1,
      isActive: userItem.is_active === "1" || userItem.is_active === 1,
      pointsBalance: userItem.points_balance || "0",
      linkedCardsCount: userItem.linked_cards_count || 0,
      vouchersCount: userItem.vouchers_count || 0,
      referralsCount: userItem.referrals_count || 0,
      gamesCompleted: userItem.games_completed || 0,
    }));

  const fetchUsers = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminAllUsers}?${buildQueryParams(pageNum)}`);

      if (response.data?.status || response.data?.success) {
        const data = response.data.data;
        const newUsers = data.data || [];
        const transformedUsers = transformUserData(newUsers);

        if (isLoadMore) {
          setUsers((prev) => [...prev, ...transformedUsers]);
        } else {
          setUsers(transformedUsers);
        }

        const currentPage = data.current_page || 1;
        const lastPage = data.last_page || 1;
        setHasMore(currentPage < lastPage);
        setPage(currentPage);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toastr.error("Failed to fetch users");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchUsers(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleDelete = (userId) => {
    setUserToDelete(userId);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      const response = await del(endpoints.adminDeleteUser.replace("{{user_id}}", userToDelete));
      if (response.data?.success || response.data?.status) {
        toastr.success("User deleted successfully");
        setShowDeleteModal(false);
        setUserToDelete(null);
        fetchUsers(1, false);
      }
    } catch (error) {
      console.error("Failed to delete user:", error);
      toastr.error("Failed to delete user");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.adminExport}?type=members`);
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'members.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toastr.success("Export successful!");
    } catch (error) {
      console.error("Failed to export data:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    }
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "created_at",
      options: [
        { value: "name", label: "Name" },
        { value: "email", label: "Email" },
        { value: "points_balance", label: "Points Balance" },
        { value: "linked_card", label: "Linked Card" },
        { value: "vouchers_count", label: "Vouchers Count" },
        { value: "referrals_count", label: "Referrals Count" },
        { value: "games_completed", label: "Games Completed" },
        { value: "last_login_at", label: "Last Login At" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchUsers(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 className="rp-card__title" style={{ margin: 0 }}>Active Members</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of active member metrics and activity.</p>
            </div>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <SecondaryButton
                variant="orange"
                icon={tickIcon}
                onClick={() => navigate("/admin/users/create")}
              >
                Add Member
              </SecondaryButton>
            </div>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by name or email"
            filters={filters}
            showExport
            onExport={handleExport}
          />

          <DataTable
            columns={columns}
            rows={users}
            getRowKey={(r) => r.id}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Member"
        message="Are you sure you want to delete this member? This action cannot be undone."
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        itemName="member"
      />
    </div>
  );
};

AdminUsersPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminUsersPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminUsersPage;
